/**
 * IndexedDB Database Schema
 *
 * Uses Dexie for typed IndexedDB access.
 */

import Dexie, { type Table } from 'dexie'
import type { Room, Role } from '@/lib/types'

// ============= Database Types =============

export interface StoredRoom {
  id: string
  name: string
  centerLat: number
  centerLng: number
  radiusMeters: number
  createdAt: Date
  joinedAt: Date
  secret: string
  lastAccessed: Date
  pin?: string // Optional PIN for room access
}

export interface StoredIdentity {
  roomId: string
  peerId: string
  displayName: string
  publicKey: string // Base64
  privateKey: string // Base64
  role: Role
  createdAt: Date
}

export interface StoredMessage {
  id: string
  roomId: string
  type: 'text' | 'voice' | 'poi' | 'system'
  senderId: string
  senderName: string
  senderRole: Role
  timestamp: Date
  content?: string
  audioBlob?: Uint8Array
  duration?: number
  transcript?: string
  transcriptConfidence?: number
  poiType?: string
  locationLat?: number
  locationLng?: number
  address?: string
  expiresAt?: Date
}

export interface CachedMapTile {
  key: string // "{z}/{x}/{y}"
  data: Blob
  cachedAt: Date
  accessedAt: Date
}

export interface OfflineQueueItem {
  id: string
  type: 'message' | 'poi' | 'speaking'
  roomId: string
  data: string // JSON serialized
  createdAt: Date
  attempts: number
}

export interface AppSettings {
  id: 'settings'
  displayName?: string
  preferredLanguage?: string
  enableNotifications?: boolean
  autoJoinLastRoom?: boolean
  mapTilesCacheSize?: number
}

// ============= Database Class =============

export class CrowdTalkieDB extends Dexie {
  rooms!: Table<StoredRoom>
  identities!: Table<StoredIdentity>
  messages!: Table<StoredMessage>
  mapTiles!: Table<CachedMapTile>
  offlineQueue!: Table<OfflineQueueItem>
  settings!: Table<AppSettings>

  constructor() {
    super('crowdtalkie')

    this.version(1).stores({
      rooms: 'id, name, lastAccessed',
      identities: 'roomId',
      messages: 'id, roomId, timestamp, [roomId+timestamp]',
      mapTiles: 'key, accessedAt',
      offlineQueue: 'id, type, roomId, createdAt',
      settings: 'id',
    })
  }
}

// Singleton database instance
export const db = new CrowdTalkieDB()

// ============= Room Operations =============

export async function saveRoom(room: Room & { pin?: string }, secret: string): Promise<void> {
  await db.rooms.put({
    id: room.id,
    name: room.name,
    centerLat: room.center.latitude,
    centerLng: room.center.longitude,
    radiusMeters: room.radiusMeters,
    createdAt: room.createdAt,
    joinedAt: new Date(),
    secret,
    lastAccessed: new Date(),
    pin: room.pin,
  })
}

export async function getRoom(roomId: string): Promise<StoredRoom | undefined> {
  return db.rooms.get(roomId)
}

export async function getRecentRooms(limit: number = 10): Promise<StoredRoom[]> {
  return db.rooms.orderBy('lastAccessed').reverse().limit(limit).toArray()
}

export async function updateRoomAccess(roomId: string): Promise<void> {
  await db.rooms.update(roomId, { lastAccessed: new Date() })
}

export async function deleteRoom(roomId: string): Promise<void> {
  await db.transaction('rw', [db.rooms, db.identities, db.messages], async () => {
    await db.rooms.delete(roomId)
    await db.identities.delete(roomId)
    await db.messages.where('roomId').equals(roomId).delete()
  })
}

// ============= Identity Operations =============

export async function saveIdentity(identity: StoredIdentity): Promise<void> {
  await db.identities.put(identity)
}

export async function getIdentity(
  roomId: string
): Promise<StoredIdentity | undefined> {
  return db.identities.get(roomId)
}

export async function deleteIdentity(roomId: string): Promise<void> {
  await db.identities.delete(roomId)
}

// ============= Message Operations =============

export async function saveMessage(message: StoredMessage): Promise<void> {
  await db.messages.put(message)
}

export async function getMessages(
  roomId: string,
  limit: number = 100
): Promise<StoredMessage[]> {
  return db.messages
    .where('roomId')
    .equals(roomId)
    .reverse()
    .limit(limit)
    .toArray()
}

export async function deleteOldMessages(
  roomId: string,
  olderThan: Date
): Promise<number> {
  return db.messages
    .where('[roomId+timestamp]')
    .between([roomId, new Date(0)], [roomId, olderThan])
    .delete()
}

// ============= Settings Operations =============

export async function getSettings(): Promise<AppSettings> {
  const settings = await db.settings.get('settings')
  return settings ?? { id: 'settings' }
}

export async function updateSettings(
  updates: Partial<Omit<AppSettings, 'id'>>
): Promise<void> {
  const current = await getSettings()
  await db.settings.put({ ...current, ...updates })
}

// ============= Offline Queue Operations =============

export async function queueOfflineAction(
  item: Omit<OfflineQueueItem, 'id' | 'createdAt' | 'attempts'>
): Promise<string> {
  const id = crypto.randomUUID()
  await db.offlineQueue.add({
    ...item,
    id,
    createdAt: new Date(),
    attempts: 0,
  })
  return id
}

export async function getOfflineQueue(): Promise<OfflineQueueItem[]> {
  return db.offlineQueue.orderBy('createdAt').toArray()
}

export async function removeFromOfflineQueue(id: string): Promise<void> {
  await db.offlineQueue.delete(id)
}

export async function incrementQueueAttempts(id: string): Promise<void> {
  const item = await db.offlineQueue.get(id)
  if (item) {
    await db.offlineQueue.update(id, { attempts: item.attempts + 1 })
  }
}
